import React, { Component, memo } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { compose } from 'redux';
import { nanoid } from 'nanoid';
import { Helmet } from 'react-helmet-async';
import loadable from '@loadable/component';
import { actions as routerActions } from 'modules/Router';
import Media from 'components/Media';
import Api from 'services/Api';
import Bingo from 'modules/Bingo';
import Modals from 'modules/Modals';
import User from 'modules/User';
import Carousel from 'modules/Carousel';
import ChatModule from 'modules/Chat';
import MainLayout from 'components/MainLayout';
import Chat from 'components/Chat';
import makeModal from 'components/Modals';
import Announcements from 'components/Notifications/Announcements';
import GameLoadingScreen, { GameVariant } from 'components/GameLoadingScreen';
import requiresData from 'components/RequiresData';
import BingoModals from './BingoModals';
import BingoInfoPanel from './src/BingoInfoPanel';
import BingoMiddle from './src/BingoMiddle';
import CarouselSideEffects from './src/CarouselSideEffects';
import BingoMiddleMobile from './src/BingoMiddleMobile';
import './bingo.css';

const { replace } = routerActions;

const FRESHNESS = 4000;

const BingoSounds = loadable(() =>
  import(/* webpackChunkName: "BingoSounds" */ 'components/Bingo/BingoSounds')
);

const getPreloader = (initialDelay) =>
  makeModal(
    'gamePreloader',
    { name: 'modal-fade', timeout: 200 },
    {
      expiration: initialDelay,
      pinned: true,
      blocking: false
    }
  )(GameLoadingScreen);

export class _Bingo extends Component {
  constructor(props) {
    super(props);
    const { initialDelay } = props;
    this.Preloader = initialDelay ? memo(getPreloader(initialDelay)) : null;
    this.redirecting = false;
  }

  componentDidMount() {
    const { initialize } = this.props;
    initialize();
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    if (nextProps.initialDelay !== this.props.initialDelay) {
      this.Preloader = nextProps.initialDelay ? memo(getPreloader(nextProps.initialDelay)) : null;
    }
  }

  componentDidUpdate(prevProps) {
    const { notFound, redirect404, refName, initialize } = this.props;

    if (refName && prevProps.refName && prevProps.refName !== refName) {
      // Navigated to new room
      initialize(refName);
    }

    if (notFound && !this.redirecting) {
      this.redirecting = true;
      redirect404();
    }
  }

  componentWillUnmount() {
    this.props.terminate();
  }

  render() {
    const { type, refName: urlRefName } = this.props.match.params;
    const { refName, backgroundImageUrl, initialDelay, name, chatDisabled, hasLoaded } = this.props;
    const Preloader = this.Preloader;
    const renderedPreloader = Preloader && (
      <Preloader
        key="preloader"
        isActive
        interval={initialDelay}
        gameRef={refName}
        gameVariant={GameVariant.Bingo}
      />
    );

    return (
      <MainLayout
        className={`bingo g${type}`}
        left={<BingoInfoPanel refName={refName || urlRefName} type={type} />}
        middle={[
          renderedPreloader,
          <Announcements key="announcements" />,
          <Media key="bingoMiddleCol" query="(max-width: 768px)">
            {(matches) =>
              matches ? (
                <BingoMiddleMobile refName={refName || urlRefName} />
              ) : (
                <BingoMiddle refName={refName || urlRefName} />
              )
            }
          </Media>
        ]}
        right={
          !hasLoaded ? null : !chatDisabled ? <Chat gameChannel={refName || urlRefName} /> : null
        }
        backgroundImageUrl={backgroundImageUrl}
      >
        <Helmet>
          <title>{name} | MrQ</title>
        </Helmet>
        <BingoSounds />
        <BingoModals />
        <CarouselSideEffects refName={refName} />
      </MainLayout>
    );
  }
}

_Bingo.propTypes = {
  match: PropTypes.object.isRequired,
  refName: PropTypes.string,
  name: PropTypes.string,
  initialize: PropTypes.func.isRequired,
  terminate: PropTypes.func.isRequired,
  initialDelay: PropTypes.number,
  backgroundImageUrl: PropTypes.string,
  chatDisabled: PropTypes.bool,
  hasLoaded: PropTypes.bool.isRequired
};

_Bingo.defaultProps = {
  refName: '',
  name: '',
  initialDelay: null,
  backgroundImageUrl: '',
  chatDisabled: true
};

const mapStateToProps = (state) => {
  const refName = Bingo.selectors.getActiveRef(state);
  const roomProps = Bingo.selectors.getDisplayProps(state) || {};
  const kycStatus = User.selectors.getKycStatus(state);
  const chatDisabled = User.selectors.model(state).chatDisabled;
  const { backgroundImageUrl, initialDelay, name } = roomProps;
  const notFound = Bingo.selectors.getNotFound(state);
  const defaultChatChannel = ChatModule.selectors.getDefaultChannelRef(state);

  return {
    backgroundImageUrl,
    initialDelay,
    notFound,
    name,
    refName,
    chatDisabled,
    kycStatus,
    defaultChatChannel
  };
};

const mapDispatchToProps = (dispatch, ownProps) => ({
  initialize: async (refName = ownProps.match.params.refName) => {
    dispatch(Bingo.actions.setActive(refName));
    dispatch(Bingo.actions.refresh(dispatch, refName));
    dispatch(Bingo.actions.subscribe(refName));
    Api.actions.user.getSettings()(dispatch);

    const chatAvailability = await Api.actions.user.getChatAvailability()(dispatch);
    if (!chatAvailability?.data?.chatDisabled) {
      Api.actions.chat.listRooms()(dispatch);
    }
  },
  updateStartDate: () =>
    Api.actions.bingo.startDate(
      { refName: ownProps.match.params.refName, instance: 'next' },
      undefined,
      {
        skipSession: true
      }
    )(dispatch),
  updateMute: (mute) => dispatch(Bingo.actions.mute(mute)),
  redirect404: () => {
    dispatch(replace(`/secure/lobby`));
    dispatch(
      Modals.actions.create(nanoid(), {
        notification: true,
        creationDate: Date.now(),
        message: 'Room does not exist. Please choose one of our wonderful rooms below!',
        expiration: 60000,
        announcement: true,
        pinned: true,
        blocking: false,
        closeOnNavigation: true,
        severity: 1,
        destroyOnClose: true
      })
    );
  },
  terminate: () => {
    dispatch(Bingo.actions.setActive(''));
    dispatch(Bingo.actions.unsubscribe(ownProps.match.params.refName));
    dispatch(Carousel.actions.reset('bingoMiddle'));
    dispatch(Carousel.actions.reset('bingoMiddleMobile'));
    dispatch(Modals.actions.close('gamePreloader'));
  }
});

export default compose(
  connect(mapStateToProps, mapDispatchToProps),
  requiresData(User.actionTypes.AT.GET_CHAT_AVAILABILITY._, FRESHNESS)
)(_Bingo);
